import React, { useState, useRef, Suspense, useCallback } from 'react';
import { 
  CameraIcon, 
  PhotoIcon, 
  SparklesIcon,
  TagIcon,
  CurrencyDollarIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';
import { analyzeImageWithGeminiAPI, uploadImageDirectly } from '../services/apiService';
import { scanService } from '../services/scanService';
import { statsEmitter } from '../utils/statsEmitter';
import { AnalysisResult, ApiProvider } from '../types';
import { ProductCategory } from '../config/analysisPrompts';
import LoadingSpinner from '../components/LoadingSpinner';
import CategorySelector from '../components/CategorySelector';
import MultiAngleCarousel from '../components/MultiAngleCarousel';

const CameraPage: React.FC = () => {
  const { showToast } = useToast();
  const { isAuthenticated } = useAuth();
  
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<ProductCategory>(ProductCategory.AUTO_DETECT);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const handleImageSelected = useCallback(async (file: File) => {
    setSelectedImage(file);
    setImagePreview(URL.createObjectURL(file));
    setIsCameraActive(false);
    showToast('success', 'Image Selected', 'Ready for analysis');
  }, [showToast]);

  const analyzeImage = useCallback(async () => {
    if (!selectedImage) return;

    setIsUploading(true);
    setIsAnalyzing(true);

    try {
      const imageUrl = await uploadImageDirectly(selectedImage);
      const result = await analyzeImageWithGeminiAPI(imageUrl);
      setAnalysisResult(result);
      showToast('success', 'Analysis Complete!', 'Your image has been analyzed successfully.');

      // Save to database if user is authenticated
      if (isAuthenticated) {
        try {
          await scanService.createScan({
            title: result.title,
            description: result.description,
            value: result.value,
            aiExplanation: result.aiExplanation,
            imageUrl: imageUrl,
            apiProvider: ApiProvider.GEMINI,
            visualMatches: result.visualMatches || [],
          });
          statsEmitter.emit();
        } catch (saveError) {
          console.error('Failed to save scan:', saveError);
          // Don't show error to user as analysis was successful
        }
      }
    } catch (error) {
      console.error('Analysis error:', error);
      showToast('error', 'Analysis Failed', 'Please try again.');
    } finally {
      setIsUploading(false);
      setIsAnalyzing(false);
    }
  }, [selectedImage, showToast, isAuthenticated]);

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setIsCameraActive(true);
        showToast('success', 'Camera Ready', 'Position your item and tap capture');
      }
    } catch (error) {
      console.error('Camera error:', error);
      showToast('error', 'Camera Error', 'Unable to access camera');
    }
  }, [showToast]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsCameraActive(false);
  }, []);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
        handleImageSelected(file);
        stopCamera();
      }
    }, 'image/jpeg', 0.8);
  }, [handleImageSelected, stopCamera]);

  const resetAnalysis = useCallback(() => {
    setSelectedImage(null);
    setImagePreview(null);
    setAnalysisResult(null);
    setIsUploading(false);
    setIsAnalyzing(false);
    stopCamera();
  }, [stopCamera]);

  const handleSaveAnalysis = useCallback(async () => {
    if (!analysisResult) return;

    if (!isAuthenticated) {
      showToast('error', 'Please sign in to save analyses', 'You need to be logged in to save your analysis results.');
      return;
    }

    try {
      await scanService.createScan({
        title: analysisResult.title,
        description: analysisResult.description,
        value: analysisResult.value,
        aiExplanation: analysisResult.aiExplanation,
        imageUrl: analysisResult.imageUrl || imagePreview || undefined,
        apiProvider: ApiProvider.GEMINI,
        visualMatches: analysisResult.visualMatches || [],
      });
      statsEmitter.emit();
      showToast('success', 'Analysis Saved', 'Analysis saved to your history.');
    } catch (error) {
      console.error('Save error:', error);
      showToast('error', 'Save Failed', 'Unable to save analysis. Please try again.');
    }
  }, [analysisResult, imagePreview, showToast, isAuthenticated]);

  return (
    <>
      {/* Fullscreen Camera Interface */}
      {isCameraActive && (
        <div className="fixed inset-0 z-50 bg-black">
          {/* Camera Status Bar */}
          <div className="absolute top-0 left-0 right-0 z-10 bg-black/50 backdrop-blur-sm">
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                <span className="text-white font-medium">Camera Active</span>
              </div>
              <button
                onClick={stopCamera}
                className="w-10 h-10 bg-black/30 hover:bg-black/50 text-white rounded-full flex items-center justify-center transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Camera Feed */}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
          />

          {/* Camera Grid Overlay */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="w-full h-full grid grid-cols-3 grid-rows-3">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="border border-white/20"></div>
              ))}
            </div>
          </div>

          {/* Corner Focus Frames */}
          <div className="absolute top-20 left-6 w-8 h-8 border-t-2 border-l-2 border-white/60"></div>
          <div className="absolute top-20 right-6 w-8 h-8 border-t-2 border-r-2 border-white/60"></div>
          <div className="absolute bottom-32 left-6 w-8 h-8 border-b-2 border-l-2 border-white/60"></div>
          <div className="absolute bottom-32 right-6 w-8 h-8 border-b-2 border-r-2 border-white/60"></div>

          {/* Auto-enhance Toggle */}
          <div className="absolute bottom-32 left-6">
            <div className="bg-black/50 backdrop-blur-sm rounded-lg px-3 py-2">
              <div className="flex items-center space-x-2">
                <div className="w-1 h-4 bg-blue-500 rounded-full"></div>
                <span className="text-white text-sm font-medium">Auto-enhance</span>
              </div>
              <div className="text-white text-sm">ON</div>
            </div>
          </div>

          {/* Settings Button */}
          <button className="absolute bottom-32 right-6 w-12 h-12 bg-black/50 hover:bg-black/70 backdrop-blur-sm text-white rounded-full flex items-center justify-center transition-all duration-200">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>

          {/* Camera Controls */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
            <button
              onClick={capturePhoto}
              className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-200 ring-4 ring-white/20"
            >
              <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center">
                <CameraIcon className="h-8 w-8 text-white" />
              </div>
            </button>
          </div>

          <canvas ref={canvasRef} className="hidden" />
        </div>
      )}

      {/* Regular Page Content */}
      <div className="min-h-screen bg-gray-900 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-3 rounded-full">
                <CameraIcon className="h-8 w-8 text-white" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              Camera Analysis
            </h1>
            <p className="text-gray-400">
              Take a photo or upload an image for instant AI analysis
            </p>
          </div>

          {/* Main Content Card */}
          <div className="bg-gray-800 rounded-2xl shadow-lg border border-gray-700 overflow-hidden">
          
          {/* Camera/Upload Section */}
          {!selectedImage && !isCameraActive ? (
            <div className="p-8">
              <div className="text-center mb-6">
                <h2 className="text-xl font-semibold text-white mb-2">Get Started</h2>
                <p className="text-gray-400">Choose how you'd like to capture your item</p>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={startCamera}
                  className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 space-x-3"
                >
                  <CameraIcon className="h-6 w-6" />
                  <span>Open Camera</span>
                </button>

                <label className="inline-flex items-center justify-center px-8 py-4 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 space-x-3 cursor-pointer">
                  <PhotoIcon className="h-6 w-6" />
                  <span>Upload Image</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleImageSelected(file);
                      }
                    }}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
          ) : selectedImage ? (
            /* Image Preview & Analysis Section */
            <div className="p-8">
              {/* Image Preview */}
              <div className="bg-gray-700/50 rounded-xl p-6 border border-gray-600 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">Selected Image</h3>
                  <button
                    onClick={resetAnalysis}
                    className="text-red-400 hover:text-red-300 transition-colors"
                  >
                    Remove Image & Start Over
                  </button>
                </div>
                <div className="relative">
                  <img
                    src={imagePreview || ''}
                    alt="Selected item"
                    className="w-full h-64 object-contain bg-gray-800 rounded-lg border border-gray-600"
                  />
                </div>
              </div>

              {/* Category Selection */}
              <div className="mb-6">
                <Suspense fallback={<LoadingSpinner />}>
                  <CategorySelector
                    selectedCategory={selectedCategory}
                    onCategoryChange={setSelectedCategory}
                  />
                </Suspense>
              </div>

              {/* Analyze Button */}
              <div className="text-center">
                <button
                  onClick={analyzeImage}
                  disabled={isUploading || isAnalyzing}
                  className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 space-x-3 disabled:opacity-50 disabled:transform-none"
                >
                  {isUploading ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span>Uploading...</span>
                    </>
                  ) : isAnalyzing ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span>Analyzing...</span>
                    </>
                  ) : (
                    <>
                      <SparklesIcon className="h-6 w-6" />
                      <span>Analyze with AI</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : null}


        </div>

        {/* Analysis Results Section */}
        {analysisResult && (
          <div className="mt-8 bg-gray-800 rounded-2xl shadow-lg border border-gray-700 overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-white">Analysis Complete</h2>
                  <p className="text-green-100">Here's what our AI found about your item</p>
                </div>
                <button
                  onClick={handleSaveAnalysis}
                  className="inline-flex items-center px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors space-x-2"
                >
                  <DocumentTextIcon className="h-5 w-5" />
                  <span>Save Analysis</span>
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {/* Image Comparison */}
              {analysisResult.imageCompare && (
                <div className="mb-8">
                  <Suspense fallback={<LoadingSpinner />}>
                    <MultiAngleCarousel 
                      images={{ comparison: analysisResult.imageCompare }}
                      userImage={imagePreview || ''}
                      className=""
                    />
                  </Suspense>
                </div>
              )}

              {/* Item Details Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column - Basic Info */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                      <TagIcon className="h-5 w-5 mr-2 text-blue-400" />
                      Item Information
                    </h3>
                    <div className="bg-blue-900/30 rounded-xl p-6 border border-blue-700">
                      <h4 className="text-xl font-bold text-white mb-2">{analysisResult.title}</h4>
                      <div className="space-y-3">
                        <div>
                          <h5 className="font-medium text-blue-300 mb-1">Description</h5>
                          <p className="text-gray-300">{analysisResult.description}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Value */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                      <CurrencyDollarIcon className="h-5 w-5 mr-2 text-green-400" />
                      Estimated Value
                    </h3>
                    <div className="bg-green-900/30 rounded-xl p-6 border border-green-700">
                      <div className="text-3xl font-bold text-green-400 mb-2">{analysisResult.value}</div>
                      <p className="text-green-200 text-sm">Based on current market data</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={resetAnalysis}
                  className="inline-flex items-center justify-center px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Analyze Another Item
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </>
  );
};

export default CameraPage;
